import { Field, Label, Select } from "@digdir/designsystemet-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useId } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormWrapper, TranslatedValidationMessage } from "@/components";
import {
  type BestillingPageKey,
  type BestillingPeriode,
  useBestillingContext,
} from "@/features/bestillinger";
import {
  BestillingNavigationActions,
  FormNavigationBlocker,
  useBestillingNavigation,
} from "@/features/navigation";
import { isDirty } from "@/utils";
import {
  AvtalePeriodeSchemaMapper,
  type PeriodeSchema,
  periodeSchema,
} from "./AvtalePeriodeFormSchemas";

type AvtalePeriodeFormProps = {
  perioder: BestillingPeriode[];
};
const currentPageKey: BestillingPageKey = "/bestilling/ny/periode";

export function AvtalePeriodeForm({ perioder }: AvtalePeriodeFormProps) {
  const { t } = useTranslation();

  const { navigateNext } = useBestillingNavigation(currentPageKey);
  const { readOnly, bestilling, setBestillingData } = useBestillingContext();

  const form = useForm({
    resolver: zodResolver(periodeSchema),
    values: AvtalePeriodeSchemaMapper.toPeriodeSchema(bestilling.avtalePeriode),
    disabled: readOnly,
    mode: "all",
  });

  const updateBestillingQuery = (values: PeriodeSchema) => {
    return Promise.resolve(
      setBestillingData({
        ...bestilling,
        avtalePeriode: AvtalePeriodeSchemaMapper.toBestillingPeriode(values),
      }),
    );
  };

  async function handleSubmit(formData: PeriodeSchema) {
    if (isDirty(form.formState)) {
      await updateBestillingQuery(formData);
    }
    await navigateNext();
  }
  const selectId = useId();
  return (
    <section>
      <FormNavigationBlocker form={form} onSubmit={updateBestillingQuery} />
      <FormWrapper onSubmit={form.handleSubmit(handleSubmit)}>
        <Field>
          <Label htmlFor={selectId}>
            {t("pages.periode.velg-periode-label")}
          </Label>
          <Select
            {...form.register("selectedPeriode")}
            id={selectId}
            disabled={readOnly}
          >
            <Select.Option value="">
              {t("pages.periode.form.default-option")}
            </Select.Option>
            {perioder?.map((periode) => (
              <Select.Option
                key={AvtalePeriodeSchemaMapper.toSelectOptionValue(periode)}
                value={AvtalePeriodeSchemaMapper.toSelectOptionValue(periode)}
              >
                {AvtalePeriodeSchemaMapper.toSelectOptionLabel(periode)}
              </Select.Option>
            ))}
          </Select>
          <TranslatedValidationMessage
            error={form.formState.errors.selectedPeriode}
          />
        </Field>

        <BestillingNavigationActions
          currentPageKey={currentPageKey}
          disableNext={readOnly}
        />
      </FormWrapper>
    </section>
  );
}
